import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { DEFAULT_TENANT_THEME } from './defaultTenantTheme';
import { fetchTenantConfig, resolveTenantId } from './tenantConfigService';

const TenantThemeContext = createContext({
  tenantId: DEFAULT_TENANT_THEME.tenantId,
  tenantConfig: DEFAULT_TENANT_THEME,
  brandColors: {}
});

const toRgba = (hex, alpha) => {
  const normalized = hex.replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized;

  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getAccessibleTextColor = (hex) => {
  const normalized = hex.replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized;

  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? '#212529' : '#F9FCFF';
};

const getPaletteMode = (surfaceHex) => {
  const normalized = surfaceHex.replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized;

  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? 'light' : 'dark';
};

const buildBrandColors = (tenantConfig) => {
  const color = tenantConfig.tokens.color;
  return {
    primary: color.primary,
    secondary: color.secondary,
    accent: color.secondary,
    text: color.text,
    darkText: '#212529',
    onPrimary: getAccessibleTextColor(color.primary),
    onSecondary: getAccessibleTextColor(color.secondary),
    button: color.primary,
    background: color.surface,
    cardBackground: '#FFFFFF',
    successGreen: color.success,
    successBackground: toRgba(color.success, 0.14),
    borderColor: toRgba(color.text, 0.18),
    error: color.error
  };
};

const applyCssVars = (tenantConfig) => {
  const root = document.documentElement;
  const { color, radius, spacing, font } = tenantConfig.tokens;

  root.style.setProperty('--tenant-color-primary', color.primary);
  root.style.setProperty('--tenant-color-secondary', color.secondary);
  root.style.setProperty('--tenant-color-surface', color.surface);
  root.style.setProperty('--tenant-color-text', color.text);
  root.style.setProperty('--tenant-color-success', color.success);
  root.style.setProperty('--tenant-color-error', color.error);

  root.style.setProperty('--tenant-radius-sm', `${radius.sm}px`);
  root.style.setProperty('--tenant-radius-md', `${radius.md}px`);
  root.style.setProperty('--tenant-radius-lg', `${radius.lg}px`);

  root.style.setProperty('--tenant-spacing-xs', `${spacing.xs}px`);
  root.style.setProperty('--tenant-spacing-sm', `${spacing.sm}px`);
  root.style.setProperty('--tenant-spacing-md', `${spacing.md}px`);
  root.style.setProperty('--tenant-spacing-lg', `${spacing.lg}px`);

  root.style.setProperty('--tenant-font-heading', font.heading);
  root.style.setProperty('--tenant-font-body', font.body);
};

export const TenantThemeProvider = ({ children }) => {
  const [tenantConfig, setTenantConfig] = useState(DEFAULT_TENANT_THEME);
  const [tenantId, setTenantId] = useState(DEFAULT_TENANT_THEME.tenantId);

  useEffect(() => {
    const loadTenantConfig = async () => {
      const resolvedTenantId = resolveTenantId();
      setTenantId(resolvedTenantId);
      const config = await fetchTenantConfig(resolvedTenantId);
      setTenantConfig(config);
    };

    loadTenantConfig();
  }, []);

  useEffect(() => {
    applyCssVars(tenantConfig);
  }, [tenantConfig]);

  const muiTheme = useMemo(() => createTheme({
    palette: {
      mode: getPaletteMode(tenantConfig.tokens.color.surface),
      primary: {
        main: tenantConfig.tokens.color.primary,
        contrastText: getAccessibleTextColor(tenantConfig.tokens.color.primary)
      },
      secondary: {
        main: tenantConfig.tokens.color.secondary,
        contrastText: getAccessibleTextColor(tenantConfig.tokens.color.secondary)
      },
      background: { default: tenantConfig.tokens.color.surface },
      text: { primary: tenantConfig.tokens.color.text },
      success: { main: tenantConfig.tokens.color.success },
      error: { main: tenantConfig.tokens.color.error }
    },
    shape: {
      borderRadius: tenantConfig.tokens.radius.md
    },
    typography: {
      fontFamily: tenantConfig.tokens.font.body,
      h1: { fontFamily: tenantConfig.tokens.font.heading },
      h2: { fontFamily: tenantConfig.tokens.font.heading },
      h3: { fontFamily: tenantConfig.tokens.font.heading },
      h4: { fontFamily: tenantConfig.tokens.font.heading },
      h5: { fontFamily: tenantConfig.tokens.font.heading },
      h6: { fontFamily: tenantConfig.tokens.font.heading }
    }
  }), [tenantConfig]);

  const brandColors = useMemo(() => buildBrandColors(tenantConfig), [tenantConfig]);

  const value = useMemo(() => ({
    tenantId,
    tenantConfig,
    brandColors
  }), [tenantId, tenantConfig, brandColors]);

  return (
    <TenantThemeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </TenantThemeContext.Provider>
  );
};

export const useTenantTheme = () => useContext(TenantThemeContext);
